import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { fade } from '@material-ui/core';
import { dataAttrs } from 'Helpers/Testing';
import { TextField } from '../../../ExternalLibraries';

const styles = (theme) => ({
  root: {
    '& .MuiInputBase-root': {
      fontSize: theme.typography.h5.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
    },
    '& .MuiInputBase-root.Mui-focused': {
      background: theme.palette.facet.accentBlue20,
    },
    '& .MuiFormLabel-root': {
      fontWeight: theme.typography.fontWeightFacetBook,
      color: theme.palette.facet.primaryBlue,
      fontSize: theme.typography.h5.fontSize,
    },
    '& .MuiFilledInput-input:-webkit-autofill': {
      borderRadius: theme.radius.md,
      margin: theme.spacing(0, 0.5),
    },
    '& .MuiInputLabel-filled': {
      transform: 'translate(20px, 25px) scale(1)',
      maxWidth: 'calc(100% - 40px)',
    },
    '& .MuiInputLabel-shrink': {
      color: theme.palette.common.black,
      transform: 'translate(12px, 15px) scale(0.75)',
    },
    // --------- Start Disabling ----------------
    '& .MuiFormLabel-root.Mui-disabled:not(.Mui-error)': {
      color: theme.palette.action.disabled,
    },
    '& .MuiInputBase-root.Mui-disabled:not(.Mui-error)': {
      color: theme.palette.action.disabled,
      backgroundColor: theme.palette.action.disabledBackground,
      '&:hover': {
        color: theme.palette.action.disabled,
      },
      '& .MuiInputBase-input': {
        color: theme.palette.action.disabled,
      },
      '& .MuiSvgIcon-root': {
        color: theme.palette.action.disabled,
      },
    },
    withHelperText: {
      '& p.MuiFormHelperText-root': {
        marginBottom: theme.spacing(2),
      },
    },
    // --------- End Disabling ----------------
    // --------- Start Error ----------------
    '& .MuiFormLabel-root.Mui-error': {
      color: fade(theme.palette.error.main, 0.8),
    },
    '& .MuiInputBase-root.Mui-error': {
      color: fade(theme.palette.error.main, 0.8),
      backgroundColor: fade(theme.palette.error.main, 0.3),
      '&:hover': {
        color: fade(theme.palette.error.main, 0.8),
      },
      '& .MuiInputBase-input': {
        color: fade(theme.palette.error.main, 0.8),
      },
      '& .MuiSvgIcon-root': {
        color: fade(theme.palette.error.main, 0.8),
      },
    },
    // --------- End Error ----------------
    paddingBottom: theme.spacing(0),
  },
  textField: {
    width: '100%',
  },
  input: {
    minHeight: '67px', // <--- TODO: Make a const
    color: theme.palette.facet.primaryBlue,
    background: theme.palette.facet.accentBlue20,
    borderRadius: theme.radius.md,
    fontSize: theme.typography.h4.fontSize,
    '&:hover': {
      background: theme.palette.facet.accentBlue20,
    },
  },
  multilineInput: {
    color: theme.palette.facet.primaryBlue,
    background: theme.palette.facet.accentBlue20,
    borderRadius: theme.radius.md,
    fontSize: theme.typography.h4.fontSize,
    '&:hover': {
      background: theme.palette.facet.accentBlue20,
    },
  },
});

const TextFieldStandardized = ({
  className,
  classes,
  InputProps,
  FormHelperTextProps,
  readonly,
  hidden,
  label,
  value,
  onChange,
  onAdd,
  onRemove,
  ...rest
}) => {
  const showRequiredHelperText = rest.helperText?.props?.showRequiredHelperText ?? true;

  return (
    <TextField
      {...rest}
      classes={{
        root: classNames(classes.root, classes.textField, className, {
          [classes.withHelperText]: showRequiredHelperText,
        }),
      }}
      InputProps={{ // eslint-disable-line
        ...InputProps,
        className: classNames(InputProps?.className, {
          [classes.input]: !InputProps?.multiline,
          [classes.multilineInput]: InputProps?.multiline,
        }),
        disableUnderline: true,
      }}
      variant={'filled'}
      disabled={readonly}
      hidden={hidden}
      label={label}
      value={value}
      onChange={onChange}
      {...dataAttrs('textField')}
    />
  );
};

TextFieldStandardized.defaultProps = {
  className: '',
  InputProps: {},
  FormHelperTextProps: {},
};

TextFieldStandardized.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  InputProps: PropTypes.object,
  FormHelperTextProps: PropTypes.object,
};

export default withStyles(styles)(TextFieldStandardized);
